import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { merge, take } from 'rxjs';
import { TranslationManagerService } from '../core/i18n';
import { escapeHtml } from '../components/warehouse/warehouse.utils';
import { NotificationService } from './notifications.service';
import { activeTranslations } from '../enum/constants';

export interface FiscalNotificationData {
  kind: 'fiscal_operation';
  code: string;
  params: Record<string, string | number | null>;
  company_id?: string;
  fiscal_operation_id?: string;
  cash_register_id?: string;
  status?: string;
}

type NotificationTone = 'info' | 'success' | 'warning' | 'error';

const EVENT_TONES: Record<string, NotificationTone> = {
  FISCAL_REQUEST_ACCEPTED: 'info',
  FISCAL_RECEIPT_DELIVERY_REQUEST_ACCEPTED: 'info',
  FISCAL_OPERATION_QUEUED: 'info',
  FISCAL_RECEIPT_DELIVERY_QUEUED: 'info',
  FISCAL_OPERATION_SKIPPED: 'warning',
  FISCAL_OPERATION_MANUAL_REQUIRED: 'error',
  FISCAL_RECEIPT_DELIVERY_FAILED: 'error',
  FISCAL_SHIFT_OPENED: 'success',
  FISCAL_SHIFT_CLOSED: 'success',
  FISCAL_RECEIPT_CREATED: 'success',
  FISCAL_RETURN_CREATED: 'success',
  FISCAL_X_REPORT_CREATED: 'success',
  FISCAL_CASH_IN_COMPLETED: 'success',
  FISCAL_CASH_OUT_COMPLETED: 'success',
  FISCAL_RECEIPT_DELIVERED: 'success',
};

@Injectable({ providedIn: 'root' })
export class FiscalNotificationService {
  private readonly translate = inject(TranslateService);
  private readonly parser = inject(TranslateParser);
  private readonly translations = inject(TranslationManagerService);
  private readonly notifications = inject(NotificationService);
  private readonly translationChanges = toSignal(merge(this.translate.onLangChange, this.translate.onTranslationChange));

  constructor() {
    this.translations.loadModuleTranslations('prro').pipe(take(1)).subscribe();
  }

  isFiscalCode(code: string | undefined): code is string {
    return typeof code === 'string' && /^(FISCAL_|CASH_REGISTER_|VCHASNO_|INVALID_FISCAL_|RECEIPT_)/.test(code);
  }

  readError(error: unknown): { code?: string; params?: Record<string, unknown> } {
    const body = (error as { error?: any })?.error ?? error as any;
    const detail = body?.errors?.[0] ?? body;
    const code = detail?.code ?? (typeof body?.message === 'string' ? body.message : undefined);
    return { code, params: detail?.details ?? {} };
  }

  errorMessage(error: unknown): string {
    const { code, params } = this.readError(error);
    this.translationChanges();
    return this.isFiscalCode(code) ? this.text(`errors.${code}`, params) : this.reason(String(code));
  }

  showError(error: unknown): boolean {
    const { code, params } = this.readError(error);
    if (!this.isFiscalCode(code)) return false;
    this.show({ kind: 'fiscal_operation', code, params: params as FiscalNotificationData['params'] });
    return true;
  }

  format(data: FiscalNotificationData): { title: string; message: string; tone: NotificationTone } {
    this.translationChanges();
    const params = data.params;
    const isError = this.isErrorCode(data.code);
    const title = data.code === 'FISCAL_PAYMENT_CANCELLATION_BLOCKED'
      ? this.text('notifications.cancel_blocked_title') : this.text('title');
    const task = params['task'] !== undefined && params['task'] !== null ? this.text(`task.${params['task']}`) : '';
    const parts = [this.text(`${isError ? 'errors' : 'notifications'}.${data.code}`, { ...params, task })];
    if (params['register_name']) parts.push(this.text('notifications.register', params));
    if (params['receipt_number'] && data.status === 'succeeded') parts.push(this.text('notifications.document', params));
    if (typeof params['reason_code'] === 'string') parts.push(this.reason(params['reason_code']));
    return { title, message: parts.join(' '), tone: this.tone(data.code) };
  }

  requestAccepted(delivery = false): void {
    this.show({
      kind: 'fiscal_operation',
      code: delivery ? 'FISCAL_RECEIPT_DELIVERY_REQUEST_ACCEPTED' : 'FISCAL_REQUEST_ACCEPTED',
      params: {},
    });
  }

  show(data: FiscalNotificationData): void {
    if (!data.code) return;
    this.translations.loadModuleTranslations('prro').pipe(take(1)).subscribe(() => {
      const { title, message, tone } = this.format(data);
      const show = {
        info: this.notifications.showInfo, success: this.notifications.showSuccess,
        warning: this.notifications.showWarning, error: this.notifications.showError,
      }[tone];
      show.call(this.notifications, escapeHtml(title), escapeHtml(message), undefined, true);
    });
  }

  private reason(code: string): string {
    // Provider-specific numeric codes are displayed as data, never as server-supplied prose.
    const applicationCode = this.isFiscalCode(code) || ['TRANSPORT_ERROR', 'INVALID_RESPONSE'].includes(code);
    return applicationCode ? this.text(`errors.${code}`) : this.text('notifications.provider_error_code', { code });
  }

  private isErrorCode(code: string): boolean {
    return !Object.hasOwn(EVENT_TONES, code);
  }

  private tone(code: string): NotificationTone {
    if (code === 'FISCAL_PAYMENT_CANCELLATION_BLOCKED') return 'warning';
    return this.isErrorCode(code) ? 'error' : EVENT_TONES[code];
  }

  private text(key: string, params?: Record<string, unknown>): string {
    const template = key.split('.').reduce((group, part) => group?.[part], activeTranslations.prro);
    return this.parser.interpolate(template, params) as string;
  }
}
